import { ExchangeProduct, LineItem, ReturnFullProduct } from './types';

/**
 * Pricing helpers for the customer-facing return summary screen.
 *
 * Mirrors the backend pricing in
 * `xconnector-return-api/.../service/implementation/ReturnService.java`. Field
 * semantics differ between line items and exchange products even though both
 * carry a field called `total_discount`:
 *
 *   • `ReturnFullProduct.total_discount`  — TOTAL across the whole original
 *                                           line (Shopify contract). Per-unit
 *                                           discount is `total_discount /
 *                                           quantity`.
 *
 *   • `ExchangeProduct.total_discount`    — INFORMATIONAL per-unit savings
 *                                           versus the variant's MSRP
 *                                           (`compareAtPrice − price`,
 *                                           computed in
 *                                           `ShopifyProductModal.tsx`). NOT a
 *                                           discount to subtract from `price`
 *                                           again — `price` is already the
 *                                           variant's current sale price (what
 *                                           the customer is charged). The
 *                                           field exists for display (e.g.
 *                                           "you save X off MSRP" badges).
 *
 * Each subtotal and product total is rounded to 2 decimals with HALF_UP
 * semantics so the displayed customer total matches the value the backend
 * persists. Without this, repeating-decimal discounts would drift by a cent
 * between what the customer sees on the summary screen and what they see on
 * the post-submit confirmation.
 */

const MONEY_SCALE = 2;

/**
 * Java-style HALF_UP rounding for 2-decimal money values, matching the backend
 * `BigDecimal.setScale(2, HALF_UP)` exactly within the float range we handle.
 *
 * Two subtleties versus a naive `Math.round(v * 100) / 100`:
 *
 *   1. `Math.round` rounds half toward positive infinity (`Math.round(-0.5) === 0`),
 *      not away from zero. We compute on the absolute value and reapply the sign
 *      so negatives (more expensive exchange) round the same way as positives.
 *
 *   2. Many 2-decimal "half-cent" inputs are not exactly representable in
 *      IEEE-754: `1.005` is stored as `1.00499999…`, so `1.005 * 100` → `100.4999…`
 *      and `Math.round` rounds DOWN to `100`. Backend BigDecimal handles these
 *      inputs exactly and rounds UP to `1.01`. We bridge the gap with a small
 *      epsilon (`1e-9`) added in cent-space — large enough to absorb the IEEE
 *      representation error on multi-decimal money values (Shopify sends prices
 *      as 2-decimal strings; intermediate divisions stay well below `1e-9`),
 *      small enough that it can never bump a genuinely-below-half value across
 *      the boundary.
 */
const ROUNDING_EPSILON = 1e-9;

function roundHalfUp(value: number): number {
  const factor = Math.pow(10, MONEY_SCALE);
  const sign = value < 0 ? -1 : 1;
  const scaled = Math.abs(value) * factor;
  return (sign * Math.round(scaled + ROUNDING_EPSILON)) / factor;
}

/**
 * Per-line subtotal for a (partial) return: applies the line discount
 * proportionally to the returned quantity, rounded to cents.
 *
 * Returns 0 when the original `quantity` is missing or zero (defensive — the
 * UI should never present such an item, but guard against bad data instead of
 * dividing by zero).
 */
export function computeLineItemSubtotal(prod: ReturnFullProduct): number {
  const originalQty = prod.quantity > 0 ? prod.quantity : 0;
  if (originalQty === 0) {
    return 0;
  }
  const discountPerUnit = (prod.total_discount ?? 0) / originalQty;
  return roundHalfUp(prod.quantityReturn * (prod.price - discountPerUnit));
}

/**
 * Per-line subtotal for the chosen exchange variant, rounded to cents.
 *
 * `ExchangeProduct.price` is the variant's current sale price — that's what
 * the customer is charged for the exchange. `total_discount` on this DTO is
 * informational savings versus MSRP and must NOT be subtracted from `price`
 * (doing so would double-discount the variant).
 */
export function computeExchangeSubtotal(
  exchange: ExchangeProduct,
  qty: number
): number {
  return roundHalfUp(qty * exchange.price);
}

/**
 * Total amount to refund for one returned product, net of any chosen exchange.
 * Negative when the customer picked a more expensive exchange.
 */
export function computeReturnProductTotal(prod: ReturnFullProduct): number {
  const lineSubtotal = computeLineItemSubtotal(prod);
  const exchangeSubtotal =
    prod.exchangeProduct !== null
      ? computeExchangeSubtotal(prod.exchangeProduct, prod.quantityReturn)
      : 0;
  // Both inputs are already rounded; this final round is a no-op for ordinary
  // values but stays explicit so the invariant "every persisted/displayed
  // amount is a 2-decimal value" holds.
  return roundHalfUp(lineSubtotal - exchangeSubtotal);
}

/**
 * Sum the refund across every returned product. Use this for the "total to
 * refund" widget on the return summary screen. Each per-product value is
 * already rounded, so summing them directly matches the backend's
 * `Σ Return.price = Σ ReturnProduct.price` semantics.
 */
export function computeReturnTotalPrice(
  returnProducts: ReturnFullProduct[]
): number {
  return returnProducts.reduce(
    (total, prod) => total + computeReturnProductTotal(prod),
    0
  );
}

/**
 * Per-unit cap for filtering exchange variants: the most a customer is allowed
 * to spend per unit on an exchange when the merchant has disabled
 * "more expensive exchanges allowed". Never exceeds what the customer actually
 * paid per unit on the original line.
 *
 * The backend computes `total_price = price × quantity − total_discount` at
 * ingestion, so per-unit-paid = `total_price / quantity`. We **floor** to cents
 * (instead of HALF_UP) because this value is used as a hard ceiling: a half-up
 * rounded value can let an exchange variant slip in that costs more than the
 * customer's actual refund. Example: `total_price = 20, quantity = 3` →
 * `20/3 = 6.666…`. HALF_UP would return `6.67`, which lets the customer pick a
 * 6.67-per-unit exchange — for 3 units that is 20.01 RON against an actual
 * refund of 20.00 RON. Floor returns `6.66`, capping the 3-unit exchange total
 * at 19.98 RON, always within budget.
 *
 * The trade-off is at most a few-cent under-utilisation of the customer's
 * refund on lines with repeating-decimal allocations — acceptable because the
 * customer can always submit and the backend recomputes the actual refund.
 *
 * Returns `lineItem.price` (the raw per-unit price, undiscounted) when
 * `quantity` is missing or zero — defensive fallback that preserves legacy
 * behaviour on bad data; and falls back to `price` when `total_price` is
 * absent (TypeScript can't prove the backend always sets it).
 */
export function discountedUnitPriceCap(lineItem: LineItem): number {
  if (!lineItem.quantity || lineItem.quantity <= 0) {
    return lineItem.price;
  }
  const totalPrice = lineItem.total_price ?? lineItem.price * lineItem.quantity;
  // Floor at cents to never exceed the actual per-unit-paid amount. Add the
  // same `1e-9` epsilon used by `roundHalfUp` because IEEE-754 represents
  // many exact-cent values just below their target (e.g. `0.29 * 100 =
  // 28.999999999999996`); a raw `Math.floor` would under-cap to `0.28`.
  // The epsilon is small enough never to bump `6.666… → 6.67`.
  return (
    Math.floor((totalPrice / lineItem.quantity) * 100 + ROUNDING_EPSILON) / 100
  );
}
